import { useEffect, useMemo, useState } from 'react';
import {
    bulkUpdateWebhookErrors,
    fetchWebhookErrors,
} from '~/services/webhookErrors';
import { queryClient } from '~/main';
import { WEBHOOK_ERROR_UNREAD_COUNT_KEY } from '~/hooks/useWebhookErrorUnreadCount';
import type {
    PaginatedWebhookErrors,
    WebhookErrorListFilters,
    WebhookErrorListItem,
    WebhookErrorSource,
    WebhookErrorStatus,
} from '~/types/webhookError';
import WebhookErrorDetail from '~/components/WebhookErrorDetail';

const SOURCE_LABELS: Record<WebhookErrorSource, string> = {
    campaign_request: 'Campaign Request',
    donation: 'Donation',
    event_registration: 'Event Registration',
};

const STATUS_LABELS: Record<WebhookErrorStatus, string> = {
    new: 'New',
    acknowledged: 'Acknowledged',
    resolved: 'Resolved',
};

const ReplayBadge = ({ value }: { value: boolean | null }) => {
    if (value === null) return <span className='text-gray-400'>—</span>;
    return value ? (
        <span className='text-green-600' title='Last replay succeeded'>✓</span>
    ) : (
        <span className='text-red-600' title='Last replay failed'>✗</span>
    );
};

const StatusPill = ({ status }: { status: WebhookErrorStatus }) => {
    const colors: Record<WebhookErrorStatus, string> = {
        new: 'bg-red-100 text-red-700',
        acknowledged: 'bg-yellow-100 text-yellow-800',
        resolved: 'bg-green-100 text-green-700',
    };
    return (
        <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status]}`}
        >
            {STATUS_LABELS[status]}
        </span>
    );
};

export default function WebhookErrors() {
    const [filters, setFilters] = useState<WebhookErrorListFilters>({
        status: 'new,acknowledged',
    });
    const [data, setData] = useState<PaginatedWebhookErrors | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkStatus, setBulkStatus] = useState<WebhookErrorStatus | ''>('');
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        fetchWebhookErrors(filters)
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((e) => {
                if (!cancelled) setError(e.message ?? 'Failed to load');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [filters, refreshTick]);

    const rows: WebhookErrorListItem[] = useMemo(() => data?.results ?? [], [data]);

    // Drop selections that are no longer visible after a filter / page change.
    useEffect(() => {
        if (selectedIds.size === 0) return;
        const visible = new Set(rows.map((r) => r.id));
        let changed = false;
        const next = new Set<number>();
        selectedIds.forEach((id) => {
            if (visible.has(id)) next.add(id);
            else changed = true;
        });
        if (changed) setSelectedIds(next);
    }, [rows, selectedIds]);

    const handleFilterChange = (patch: Partial<WebhookErrorListFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
    };

    const handleRowChanged = () => setRefreshTick((t) => t + 1);

    const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
    const someSelected = selectedIds.size > 0;

    const toggleSelectAll = () => {
        setSelectedIds((prev) => {
            if (allVisibleSelected) {
                const next = new Set(prev);
                rows.forEach((r) => next.delete(r.id));
                return next;
            }
            const next = new Set(prev);
            rows.forEach((r) => next.add(r.id));
            return next;
        });
    };

    const toggleRow = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const applyBulkStatus = async () => {
        if (!bulkStatus || selectedIds.size === 0) return;
        setBulkBusy(true);
        setBulkError(null);
        try {
            await bulkUpdateWebhookErrors(Array.from(selectedIds), { status: bulkStatus });
            setSelectedIds(new Set());
            setBulkStatus('');
            setRefreshTick((t) => t + 1);
            queryClient.invalidateQueries({ queryKey: WEBHOOK_ERROR_UNREAD_COUNT_KEY });
        } catch (e) {
            setBulkError((e as Error).message);
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className='p-6'>
            <div className='flex items-center justify-between mb-6'>
                <h1 className='text-2xl font-semibold'>Webhook Errors</h1>
            </div>

            {/* Filter bar */}
            <div className='flex flex-wrap gap-3 mb-4'>
                <select
                    className='border rounded px-3 py-2'
                    value={filters.source ?? ''}
                    onChange={(e) =>
                        handleFilterChange({
                            source: (e.target.value || undefined) as WebhookErrorSource | undefined,
                        })
                    }
                >
                    <option value=''>All sources</option>
                    <option value='campaign_request'>Campaign Request</option>
                    <option value='donation'>Donation</option>
                    <option value='event_registration'>Event Registration</option>
                </select>

                <select
                    className='border rounded px-3 py-2'
                    value={filters.status ?? ''}
                    onChange={(e) => handleFilterChange({ status: e.target.value || undefined })}
                >
                    <option value='new,acknowledged'>New + Acknowledged</option>
                    <option value=''>All statuses</option>
                    <option value='new'>New</option>
                    <option value='acknowledged'>Acknowledged</option>
                    <option value='resolved'>Resolved</option>
                </select>

                <select
                    className='border rounded px-3 py-2'
                    value={filters.http_status ?? ''}
                    onChange={(e) =>
                        handleFilterChange({
                            http_status: e.target.value
                                ? (Number(e.target.value) as 400 | 500)
                                : undefined,
                        })
                    }
                >
                    <option value=''>All HTTP</option>
                    <option value='400'>400</option>
                    <option value='500'>500</option>
                </select>

                <input
                    className='border rounded px-3 py-2 flex-1 min-w-[200px]'
                    type='text'
                    placeholder='Search error message…'
                    value={filters.search ?? ''}
                    onChange={(e) => handleFilterChange({ search: e.target.value || undefined })}
                />
            </div>

            {/* Bulk action bar */}
            {someSelected && (
                <div className='flex flex-wrap items-center gap-3 mb-3 p-3 border border-stroke rounded bg-gray-50'>
                    <span className='text-sm font-medium'>
                        {selectedIds.size} selected
                    </span>
                    <select
                        className='border border-stroke rounded px-3 py-1.5 text-sm bg-white'
                        value={bulkStatus}
                        onChange={(e) =>
                            setBulkStatus(e.target.value as WebhookErrorStatus | '')
                        }
                        disabled={bulkBusy}
                    >
                        <option value=''>Mark as…</option>
                        <option value='new'>New</option>
                        <option value='acknowledged'>Acknowledged</option>
                        <option value='resolved'>Resolved</option>
                    </select>
                    <button
                        className='px-4 py-1.5 bg-primary text-white text-sm font-medium rounded hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={applyBulkStatus}
                        disabled={!bulkStatus || bulkBusy}
                    >
                        {bulkBusy ? 'Applying…' : 'Apply'}
                    </button>
                    <button
                        className='px-3 py-1.5 text-sm text-gray-700 hover:text-black hover:underline disabled:opacity-50'
                        onClick={() => setSelectedIds(new Set())}
                        disabled={bulkBusy}
                    >
                        Clear
                    </button>
                    {bulkError && <span className='text-sm text-red-700'>{bulkError}</span>}
                </div>
            )}

            {/* Table */}
            {error && <div className='text-red-600 mb-4'>{error}</div>}
            {isLoading ? (
                <div>Loading…</div>
            ) : rows.length === 0 ? (
                <div className='text-gray-500'>No webhook errors. Things are quiet.</div>
            ) : (
                <table className='w-full text-sm border-collapse'>
                    <thead>
                        <tr className='text-left border-b'>
                            <th className='py-2 pr-3 w-8'>
                                <input
                                    type='checkbox'
                                    aria-label='Select all'
                                    checked={allVisibleSelected}
                                    onChange={toggleSelectAll}
                                />
                            </th>
                            <th className='py-2 pr-3'>Created</th>
                            <th className='py-2 pr-3'>Source</th>
                            <th className='py-2 pr-3'>HTTP</th>
                            <th className='py-2 pr-3'>Error</th>
                            <th className='py-2 pr-3'>Status</th>
                            <th className='py-2 pr-3'>Replay</th>
                            <th className='py-2 pr-3'>#</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.id}
                                className={`border-b cursor-pointer hover:bg-gray-50 ${
                                    row.status === 'new'
                                        ? 'border-l-4 border-l-red-500 font-semibold'
                                        : ''
                                }`}
                                onClick={() => setSelectedId(row.id)}
                            >
                                <td
                                    className='py-2 pr-3'
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type='checkbox'
                                        aria-label={`Select error ${row.id}`}
                                        checked={selectedIds.has(row.id)}
                                        onChange={() => toggleRow(row.id)}
                                    />
                                </td>
                                <td className='py-2 pr-3 whitespace-nowrap'>
                                    {new Date(row.created_at).toLocaleString()}
                                </td>
                                <td className='py-2 pr-3'>{SOURCE_LABELS[row.source]}</td>
                                <td className='py-2 pr-3'>{row.http_status}</td>
                                <td className='py-2 pr-3 max-w-md truncate' title={row.summary || row.error_message}>
                                    {row.summary || row.error_message}
                                </td>
                                <td className='py-2 pr-3'>
                                    <StatusPill status={row.status} />
                                </td>
                                <td className='py-2 pr-3'>
                                    <ReplayBadge value={row.last_replay_succeeded} />
                                </td>
                                <td className='py-2 pr-3'>{row.replay_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Pagination */}
            {data && (data.next || data.previous) && (
                <div className='flex gap-2 mt-4'>
                    <button
                        className='px-3 py-1 border rounded disabled:opacity-50'
                        disabled={!data.previous}
                        onClick={() =>
                            setFilters((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))
                        }
                    >
                        Previous
                    </button>
                    <button
                        className='px-3 py-1 border rounded disabled:opacity-50'
                        disabled={!data.next}
                        onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    >
                        Next
                    </button>
                </div>
            )}

            {selectedId && (
                <WebhookErrorDetail
                    id={selectedId}
                    onClose={() => setSelectedId(null)}
                    onChanged={handleRowChanged}
                />
            )}
        </div>
    );
}
